import {
    ActionModal,
    FormError,
    FormLayout,
    Label,
    RadioButtonGroup,
    TextInput,
} from '@kcftech/design-system';
import {
    AnalyticsModelUpdateRequest,
    IAnalyticsModelResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import { useMutation } from '@tanstack/react-query';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    getSourceCodeRules,
    timeIntervalRules,
} from 'pages/Models/AnalyticsModelTab/shared';
import { SourceCodeInput } from 'pages/Models/AnalyticsModelTab/SourceCodeInput';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { queryClient } from 'utils/query';
import { convertDuration } from 'utils/units';

const modelStatuses = [
    { value: 'false', label: 'Approved' },
    { value: 'true', label: 'Deprecated' },
];

const sourceCodeRules = getSourceCodeRules(false);

type EditModelFormData = Readonly<{
    sourceCode: UploadFileInfo | undefined;
    timeIntervalMinutes: string;
    isDeprecated: boolean;
}>;

export type EditAnalyticsModelModalProps = Readonly<{
    model: IAnalyticsModelResponse;
    onClose: (result?: IAnalyticsModelResponse) => void;
}>;

export function EditAnalyticsModelModal({
    model,
    onClose,
}: EditAnalyticsModelModalProps) {
    const { showToast } = useNotificationContext();

    const timeIntervalMinutes = useMemo(
        () =>
            convertDuration(
                model.TriggerIntervalInSeconds,
                'seconds',
                'minutes'
            ).toString(),
        [model.TriggerIntervalInSeconds]
    );

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        register,
    } = useForm<EditModelFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            sourceCode: undefined,
            timeIntervalMinutes,
            isDeprecated: model.IsDeprecated,
        },
    });

    const { mutate: updateModel, isPending: isUpdating, isError } = useMutation({
        mutationFn: async (data: EditModelFormData) => {
            const request = new AnalyticsModelUpdateRequest({
                IsDeprecated: data.isDeprecated,
                TriggerIntervalInSeconds: convertDuration(
                    data.timeIntervalMinutes,
                    'minutes',
                    'seconds'
                ),
            });

            const result = await sdClient.updateAnalyticsModels({
                modelId: model.Id,
                body: request,
            });

            // Upload file if supplied
            const file = data.sourceCode?.getRawFile?.();
            if (file) {
                await sdClient.updateAnalyticsModelSource({
                    modelId: model.Id,
                    file: {
                        data: file,
                        fileName: file.name,
                    },
                });
            }

            return result;
        },

        onSuccess: (result) => {
            queryClient.invalidateQueries({
                queryKey: ['analytic-models'],
            });
            onClose(result);
            showToast({ type: 'success', content: 'Successfully updated model' });
        },

        onError: () => showToast({ type: 'error', content: 'Failed to update model' }),
    });

    return (
        <ActionModal
            title={`Edit Model - ${model.Name} ${model.Version}`}
            showButtons
            actionButtonText="Update"
            closeButtonText="Cancel"
            width={700}
            height={550}
            onAction={handleSubmit((data) => updateModel(data))}
            onClose={onClose}
            actionable={!isSubmitting && !isUpdating}
        >
            <FormLayout>
                <Label label="Model Status" className="mt-20">
                    <Controller
                        name="isDeprecated"
                        control={control}
                        render={({ field }) => (
                            <RadioButtonGroup
                                radioStyle="indicator"
                                layout="horizontal"
                                name="deprecated"
                                items={modelStatuses}
                                value={field.value ? 'true' : 'false'}
                                onChange={(value) => field.onChange(value === 'true')}
                                className="mt-10"
                            />
                        )}
                    />
                </Label>

                <ValidatableLabel
                    label="Model Source Code"
                    className="flex-row"
                    required
                    error={errors.sourceCode}
                >
                    <Controller
                        name="sourceCode"
                        control={control}
                        render={({ field }) => (
                            <SourceCodeInput
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                        rules={sourceCodeRules}
                    />
                </ValidatableLabel>

                <TextInput
                    required
                    label="Time Interval (minutes)"
                    placeholder="Enter time interval"
                    error={errors.timeIntervalMinutes?.message}
                    {...register('timeIntervalMinutes', timeIntervalRules)}
                />

                {isError && (
                    <div className="mt-10">
                        <FormError message="Failed to update model." />
                    </div>
                )}
            </FormLayout>
        </ActionModal>
    );
}
